/**
 * Interceptor de fetch: credentials para cookies HttpOnly;
 * em 401, tenta refresh (cookie) e reenvia. Access JWT não vai para localStorage.
 */
import { getApiBaseUrl } from './apiConfig';
import { clearAuthSession, hasAuthSession, markAuthSession } from './authSession';

const SESSION_EXPIRED_EVENT = 'painelos:session-expired';

const REFRESH_MAX_ATTEMPTS = 3;

/** @type {Promise<{ success: boolean }> | null} */
let refreshInFlight = null;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function shouldSuppressSessionExpiredModal() {
  if (typeof window === 'undefined') return true;
  const p = window.location.pathname || '';
  if (p === '/monitoramento' || p.startsWith('/monitoramento/')) return true;
  if (p === '/redefinir-senha' || p === '/esqueceusenha' || p === '/avaliar') return true;
  return false;
}

let sessionExpiredEmitCoalesce = false;

function emitSessionExpiredIfNeeded() {
  if (shouldSuppressSessionExpiredModal()) return;
  if (sessionExpiredEmitCoalesce) return;
  sessionExpiredEmitCoalesce = true;
  clearAuthSession();
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  setTimeout(() => {
    sessionExpiredEmitCoalesce = false;
  }, 0);
}

/** Uso em testes — zera o mutex entre casos. */
export function __resetAuthRefreshMutexForTests() {
  refreshInFlight = null;
  sessionExpiredEmitCoalesce = false;
}

function withApiCredentials(options = {}) {
  const next = { ...options, credentials: options.credentials ?? 'include' };
  // Cookie HttpOnly autentica; remove Bearer legado/marcador do localStorage.
  try {
    if (next.headers instanceof Headers) {
      next.headers = new Headers(next.headers);
      next.headers.delete('Authorization');
    } else if (next.headers && typeof next.headers === 'object') {
      const { Authorization, authorization, ...rest } = next.headers;
      next.headers = rest;
    }
  } catch (_) {
    /* ignore */
  }
  return next;
}

/**
 * @param {typeof fetch} originalFetch
 * @param {string} apiBase
 * @param {unknown} fetchThis
 * @returns {Promise<{ success: boolean }>}
 */
async function refreshAccessTokenWithRetries(originalFetch, apiBase, fetchThis) {
  for (let attempt = 0; attempt < REFRESH_MAX_ATTEMPTS; attempt++) {
    try {
      const refreshRes = await originalFetch.call(fetchThis, `${apiBase}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (refreshRes.status === 401) {
        return { success: false };
      }

      if (refreshRes.status === 429 || refreshRes.status >= 500) {
        if (attempt < REFRESH_MAX_ATTEMPTS - 1) {
          await sleep(200 * (attempt + 1));
        }
        continue;
      }

      let refreshData;
      try {
        refreshData = await refreshRes.json();
      } catch {
        if (attempt < REFRESH_MAX_ATTEMPTS - 1) {
          await sleep(200 * (attempt + 1));
        }
        continue;
      }

      if (refreshData.success) {
        markAuthSession();
        return { success: true };
      }

      if (refreshRes.status >= 400) {
        return { success: false };
      }

      return { success: false };
    } catch {
      if (attempt < REFRESH_MAX_ATTEMPTS - 1) {
        await sleep(200 * (attempt + 1));
      }
    }
  }
  return { success: false };
}

/**
 * @param {typeof fetch} originalFetch
 * @param {string} apiBase
 * @param {unknown} fetchThis
 */
function enqueueSharedRefresh(originalFetch, apiBase, fetchThis) {
  if (!refreshInFlight) {
    refreshInFlight = refreshAccessTokenWithRetries(originalFetch, apiBase, fetchThis).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export function setupAuthFetchInterceptor() {
  const originalFetch = window.fetch;
  const getBase = () => getApiBaseUrl();

  window.fetch = async function (url, options = {}) {
    const API_BASE = getBase();
    const isApiCall =
      typeof url === 'string' && (url.includes('/api/') || url.startsWith(API_BASE + '/api/'));
    if (isApiCall) {
      options = withApiCredentials(options);
    }

    let response = await originalFetch.call(this, url, options);

    const isAuthRoute =
      typeof url === 'string' &&
      (url.includes('/api/auth/login') ||
        url.includes('/api/auth/refresh') ||
        url.includes('/api/auth/verify') ||
        url.includes('/api/auth/logout') ||
        url.includes('/api/auth/email-code'));

    if (response.status === 401 && isApiCall && !isAuthRoute && hasAuthSession()) {
      try {
        const refreshOutcome = await enqueueSharedRefresh(originalFetch, API_BASE, this);

        if (refreshOutcome.success) {
          response = await originalFetch.call(this, url, withApiCredentials(options));
          if (response.status === 401) {
            emitSessionExpiredIfNeeded();
          }
        } else {
          emitSessionExpiredIfNeeded();
        }
      } catch {
        emitSessionExpiredIfNeeded();
      }
    }

    return response;
  };
}
